/**
 * Bug-report context capture.
 *
 * Module-level rolling buffers for console errors/warnings and route history,
 * plus a helper that snapshots the current browser context (URL, viewport,
 * user agent). Used by the in-app bug reporter so users don't have to manually
 * describe what they were doing — the report includes everything needed to
 * reproduce.
 *
 * The console wrapper is installed once at app boot from main.tsx; the route
 * tracker is installed via the useTrackRouteHistory hook in AppLayout.
 */

const CONSOLE_BUFFER_MAX = 20;
const ROUTE_HISTORY_MAX = 5;

export interface ConsoleEntry {
  level: 'error' | 'warn';
  message: string;
  stack?: string;
  timestamp: string;
}

export interface RouteEntry {
  path: string;
  timestamp: string;
}

const consoleBuffer: ConsoleEntry[] = [];
const routeHistory: RouteEntry[] = [];
let consoleCaptureInstalled = false;

function pushConsoleEntry(entry: ConsoleEntry) {
  consoleBuffer.push(entry);
  if (consoleBuffer.length > CONSOLE_BUFFER_MAX) {
    consoleBuffer.splice(0, consoleBuffer.length - CONSOLE_BUFFER_MAX);
  }
}

function stringifyArg(arg: unknown): string {
  if (arg instanceof Error) return arg.message;
  if (typeof arg === 'string') return arg;
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
}

function extractStack(args: unknown[]): string | undefined {
  for (const arg of args) {
    if (arg instanceof Error && arg.stack) return arg.stack;
  }
  return undefined;
}

/**
 * Wrap console.error and console.warn so we keep a rolling buffer of the
 * last N entries. Original behavior is preserved — Sentry/devtools still
 * see the original calls.
 */
export function installConsoleCapture() {
  if (consoleCaptureInstalled) return;
  consoleCaptureInstalled = true;

  const originalError = console.error.bind(console);
  const originalWarn = console.warn.bind(console);

  console.error = (...args: unknown[]) => {
    pushConsoleEntry({
      level: 'error',
      message: args.map(stringifyArg).join(' '),
      stack: extractStack(args),
      timestamp: new Date().toISOString(),
    });
    originalError(...args);
  };

  console.warn = (...args: unknown[]) => {
    pushConsoleEntry({
      level: 'warn',
      message: args.map(stringifyArg).join(' '),
      stack: extractStack(args),
      timestamp: new Date().toISOString(),
    });
    originalWarn(...args);
  };

  window.addEventListener('error', (event) => {
    pushConsoleEntry({
      level: 'error',
      message: event.message,
      stack: event.error?.stack,
      timestamp: new Date().toISOString(),
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    pushConsoleEntry({
      level: 'error',
      message: `Unhandled rejection: ${stringifyArg(reason)}`,
      stack: reason instanceof Error ? reason.stack : undefined,
      timestamp: new Date().toISOString(),
    });
  });
}

export function pushRoute(path: string) {
  const last = routeHistory[routeHistory.length - 1];
  if (last && last.path === path) return;
  routeHistory.push({ path, timestamp: new Date().toISOString() });
  if (routeHistory.length > ROUTE_HISTORY_MAX) {
    routeHistory.splice(0, routeHistory.length - ROUTE_HISTORY_MAX);
  }
}

export interface BugContext {
  page_url: string;
  user_agent: string;
  viewport_width: number;
  viewport_height: number;
  console_errors: ConsoleEntry[];
  route_history: RouteEntry[];
}

export function getBugContext(): BugContext {
  return {
    page_url: window.location.href,
    user_agent: navigator.userAgent,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
    console_errors: [...consoleBuffer],
    route_history: [...routeHistory],
  };
}
